import clsx from "clsx";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import toast from "react-hot-toast";
import {
  extractDataFromFactura,
  parseFacturaXml,
} from "src/parsers/parsersCentralAmerica";

export default function useInvoiceParser({
  onParse,
}: {
  onParse: (data: any) => void;
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [isParsing, setIsParsing] = useState(false);
  const toastIdRef = useRef<string | undefined>(undefined);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const onDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const onDragLeave = () => {
    setIsDragging(false);
  };

  const parseXmlFile = useCallback(async (file: File) => {
    try {
      const text = await file.text();
      const { origen, datos } = parseFacturaXml(text);
      const parsed = extractDataFromFactura(origen, datos);
      return { origen, datos, parsed };
    } catch (err) {
      console.error("Error al parsear XML:", err);
      throw err;
    }
  }, []);

  const handleFile = async (file: File) => {
    setIsParsing(true);
    toastIdRef.current = toast.loading("Procesando archivo...");

    try {
      const result = await parseXmlFile(file);
      onParse(result);
      toast.success("Archivo procesado con éxito", {
        id: toastIdRef.current,
      });
    } catch {
      toast.error("Error al procesar el archivo", {
        id: toastIdRef.current,
      });
    } finally {
      setIsParsing(false);
    }
  };

  const onDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    const files = Array.from(event.dataTransfer.files);
    const xmlFiles = files.filter((file) => file.type === "text/xml");

    if (xmlFiles.length > 0) {
      await handleFile(xmlFiles[0]);
    }
  };

  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type === "text/xml") {
      handleFile(file);
    }
    // Reset input to allow selecting the same file again
    event.target.value = "";
  };

  const triggerFileSelect = () => {
    fileInputRef.current?.click();
  };

  const draggingStyles = useMemo(() => {
    return isDragging
      ? clsx([
          "relative",
          "children-opacity-0",
          "after:content-['Suelta_el_achivo_aquí']",
          "after:text-xl",
          "after:md:text-3xl",
          "after:text-red-400 after:dark:text-lime-400",
          "after:inset-0",
          "after:absolute",
          "after:z-10",
          "after:flex",
          "after:items-center",
          "after:justify-center",
          "after:border-2",
          "after:border-dashed",
          "after:border-red-300",
        ])
      : clsx([]);
  }, [isDragging]);

  return {
    onDragLeave,
    onDragOver,
    onDrop,
    onFileChange,
    triggerFileSelect,
    fileInputRef,
    isDragging,
    draggingStyles,
    isParsing,
  };
}
